/**
 * Miniature, stylised preview of each template's layout used on the library
 * cards. These are hand-drawn abstractions (not scaled renders) so each card
 * reads instantly and the five designs look genuinely distinct.
 */
export default function TemplateThumbnail({ templateId }: { templateId: string }) {
  switch (templateId) {
    case "premium-brand":
      return <PremiumBrandThumb />;
    case "marketplace-classic":
      return <MarketplaceClassicThumb />;
    case "product-focus":
      return <ProductFocusThumb />;
    case "detailed-technical":
      return <DetailedTechnicalThumb />;
    case "soft-pastel":
      return <SoftPastelThumb />;
    case "clean-commerce":
    default:
      return <CleanCommerceThumb />;
  }
}

function CleanCommerceThumb() {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col gap-2">
      <div className="h-2 bg-emerald-600 rounded-sm w-full" />
      <div className="flex items-center gap-1.5">
        <div className="w-2.5 h-2.5 bg-emerald-600 rounded-sm" />
        <div className="h-1.5 w-10 bg-slate-200 rounded-sm" />
      </div>
      <div className="flex-1 grid grid-cols-2 gap-2">
        <div className="bg-emerald-50 rounded-sm border border-emerald-100 flex items-center justify-center">
          <div className="w-3/4 h-2/3 bg-emerald-100 rounded-sm" />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="h-1.5 w-full bg-slate-300 rounded-sm" />
          <div className="h-1.5 w-2/3 bg-slate-200 rounded-sm" />
          <div className="h-1 w-16 bg-amber-300 rounded-sm" />
          <div className="h-3 bg-emerald-600 rounded-sm mt-auto" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-5 bg-slate-100 rounded-sm border border-slate-100" />
        ))}
      </div>
    </div>
  );
}

function PremiumBrandThumb() {
  return (
    <div className="w-full h-full bg-stone-50 flex flex-col overflow-hidden">
      <div className="bg-stone-950 p-3 pb-5 flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full border border-amber-400" />
          <div className="h-1 w-10 bg-stone-600 rounded-sm" />
        </div>
        <div className="h-1.5 w-3/4 bg-stone-300 rounded-sm mt-2" />
        <div className="h-1.5 w-1/2 bg-stone-500 rounded-sm" />
        <div className="h-1 w-12 bg-amber-400/70 rounded-sm mt-1" />
      </div>
      <div className="flex-1 p-3 flex flex-col gap-1.5 -mt-2">
        <div className="h-2/5 bg-stone-300 rounded-md" />
        <div className="flex-1 grid grid-cols-2 gap-1.5">
          <div className="flex flex-col gap-1">
            <div className="h-1 w-8 bg-amber-500 rounded-sm" />
            <div className="h-1 w-full bg-stone-200 rounded-sm" />
            <div className="h-1 w-2/3 bg-stone-200 rounded-sm" />
          </div>
          <div className="bg-stone-200 rounded-sm" />
        </div>
      </div>
    </div>
  );
}

function MarketplaceClassicThumb() {
  return (
    <div className="w-full h-full bg-white p-3 flex flex-col gap-2">
      <div className="border-b border-slate-200 pb-1.5 flex flex-col gap-1">
        <div className="h-1 w-8 bg-amber-500 rounded-sm" />
        <div className="h-1.5 w-3/4 bg-slate-300 rounded-sm" />
      </div>
      <div className="flex-1 grid grid-cols-4 gap-1.5">
        <div className="col-span-3 bg-slate-100 border border-slate-200 rounded-sm flex items-center justify-center">
          <div className="w-2/3 h-2/3 bg-slate-200 rounded-sm" />
        </div>
        <div className="flex flex-col gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`flex-1 rounded-sm border ${i === 0 ? "border-amber-500" : "border-slate-200 bg-slate-50"}`} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`h-2 ${i % 2 === 0 ? "bg-slate-100" : "bg-slate-50"} rounded-sm flex items-center px-1`}>
            <div className="h-1 w-1/4 bg-slate-200 rounded-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductFocusThumb() {
  return (
    <div className="w-full h-full bg-white flex flex-col overflow-hidden">
      <div className="flex-[1.4] relative">
        <div className="absolute inset-0 bg-teal-200" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute bottom-1.5 left-2 right-2 flex flex-col gap-1">
          <div className="h-1 w-10 bg-white/80 rounded-sm" />
          <div className="h-1.5 w-2/3 bg-white rounded-sm" />
        </div>
      </div>
      <div className="flex-1 p-2.5 flex flex-col gap-1.5">
        <div className="flex flex-wrap gap-1">
          <div className="h-3 w-14 bg-teal-50 border border-teal-100 rounded-full" />
          <div className="h-3 w-10 bg-teal-50 border border-teal-100 rounded-full" />
          <div className="h-3 w-12 bg-teal-50 border border-teal-100 rounded-full" />
        </div>
        <div className="h-3 bg-teal-600 rounded-md mt-auto" />
      </div>
    </div>
  );
}

function SoftPastelThumb() {
  return (
    <div className="w-full h-full bg-rose-50 p-3 flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-300" />
        <div className="h-1.5 w-10 bg-rose-200 rounded-full" />
      </div>
      <div className="bg-white border border-rose-100 rounded-xl p-2 flex flex-col gap-1">
        <div className="h-1.5 w-2/3 bg-rose-200 rounded-full" />
        <div className="h-1 w-8 bg-rose-300 rounded-full" />
      </div>
      <div className="flex-1 grid grid-cols-2 gap-1.5">
        <div className="bg-white border border-rose-100 rounded-xl flex items-center justify-center">
          <div className="w-2/3 h-2/3 bg-rose-100 rounded-lg" />
        </div>
        <div className="flex flex-col gap-1">
          <div className="h-1.5 w-full bg-rose-200 rounded-full" />
          <div className="h-1.5 w-2/3 bg-rose-100 rounded-full" />
          <div className="h-3 bg-rose-400 rounded-full mt-auto" />
        </div>
      </div>
    </div>
  );
}

function DetailedTechnicalThumb() {
  return (
    <div className="w-full h-full bg-slate-50 p-3 flex flex-col gap-2">
      <div className="bg-white border border-slate-200 rounded-sm p-1.5 flex items-center justify-between">
        <div className="h-1.5 w-1/2 bg-slate-300 rounded-sm" />
        <div className="h-1 w-8 bg-slate-800 rounded-sm" />
      </div>
      <div className="flex-1 grid grid-cols-3 gap-1.5">
        <div className="bg-white border border-slate-200 rounded-sm flex items-center justify-center">
          <div className="w-2/3 h-2/3 bg-slate-200 rounded-sm" />
        </div>
        <div className="col-span-2 flex flex-col gap-1">
          <div className="flex gap-1">
            <div className="h-2.5 flex-1 bg-slate-800 rounded-sm" />
            <div className="h-2.5 flex-1 bg-white border border-slate-200 rounded-sm" />
            <div className="h-2.5 flex-1 bg-white border border-slate-200 rounded-sm" />
          </div>
          <div className="flex-1 bg-white border border-slate-200 rounded-sm p-1.5 flex flex-col gap-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-1.5 bg-slate-100 rounded-sm" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}